/**
 * Filter and sort utilities for SmswiserCampaignStatsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

import { VOLUUM_COLUMNS } from '../constants';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { getVoluumValueForDisplay } from './voluum';
import { orDash } from './drop';
import type { FilterConfig } from './filter-parse';

const VALID_SORT_FIELDS = new Set([
  'dropList', 'list', 'gateway', 'source', 'text', 'path', 'lander', 'offer',
  ...VOLUUM_COLUMNS.map((c) => c.key),
]);
const VALID_SORT_DIRS = new Set(['asc', 'desc'] as const);
const DEFAULT_SORT_FIELD = 'dropList';
const DEFAULT_SORT_DIR = 'desc' as const;

export type SortState = { field: string; dir: 'asc' | 'desc' } | null;

export function parseSortFromParams(
  sortByParam: string | null,
  sortDirParam: string | null,
  defaultField?: string,
  defaultDir?: 'asc' | 'desc',
): SortState {
  const fallback = defaultField && VALID_SORT_FIELDS.has(defaultField) ? defaultField : DEFAULT_SORT_FIELD;
  const field = VALID_SORT_FIELDS.has(sortByParam ?? '') ? (sortByParam as string) : fallback;
  const fallbackDir = defaultDir ?? DEFAULT_SORT_DIR;
  const dir = VALID_SORT_DIRS.has(sortDirParam as 'asc' | 'desc') ? (sortDirParam as 'asc' | 'desc') : fallbackDir;
  return { field, dir };
}

export function sortToParams(sort: SortState): { sortBy?: string; sortDir?: string } {
  if (!sort) return {};
  if (!VALID_SORT_FIELDS.has(sort.field)) return {};
  return { sortBy: sort.field, sortDir: sort.dir };
}

/** Next sort after header click: desc → asc → reset to variant default (never null). */
export function cycleStatsSort(
  current: SortState,
  field: string,
  defaultField: string,
  defaultDir: 'asc' | 'desc',
): { field: string; dir: 'asc' | 'desc' } {
  if (!current || current.field !== field) return { field, dir: 'desc' };
  if (current.dir === 'desc') return { field, dir: 'asc' };
  return { field: defaultField, dir: defaultDir };
}

export function passesFilter(row: DropsWithStatsRow, config: FilterConfig): boolean {
  const { field, operator, value } = config;
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (field === 'leadList' || field === 'list') {
    const search = trimmed.toLowerCase();
    const leads = orDash(row.drop.leadsList).toLowerCase();
    const title = (row.drop.title || '').toLowerCase();
    const gw = orDash(row.drop.smsGatewayRoute).toLowerCase();
    const sender = orDash(row.drop.senderName).toLowerCase();
    return (
      leads.includes(search) || title.includes(search) || gw.includes(search) || sender.includes(search)
    );
  }

  const numVal = parseFloat(trimmed);
  if (!Number.isFinite(numVal)) return true;
  const cellVal = getVoluumValueForDisplay(row.voluum, field);
  const rowNum = typeof cellVal === 'number' ? cellVal : null;
  if (rowNum == null || !Number.isFinite(rowNum)) return false;

  switch (operator) {
    case '>=':
      return rowNum >= numVal;
    case '<=':
      return rowNum <= numVal;
    case '>':
      return rowNum > numVal;
    case '<':
      return rowNum < numVal;
    case '=':
      return Math.abs(rowNum - numVal) < 1e-9;
    default:
      return true;
  }
}
